<template>
  <div
    class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8 font-sans text-slate-800 antialiased"
  >
    <!-- Header Section -->
    <div
      class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8"
    >
      <div>
        <div class="flex items-center gap-3">
          <div
            class="p-2.5 bg-blue-600 rounded-xl text-white shadow-md shadow-blue-500/20"
          >
            <Cpu class="w-6 h-6" />
          </div>
          <div>
            <div class="flex items-center gap-2.5">
              <h2
                class="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900"
              >
                My Devices
              </h2>
              <span
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200/60"
              >
                {{ devices.length }}
              </span>
            </div>
            <p class="text-xs sm:text-sm text-slate-500 mt-0.5">
              Monitor active hardware readers and setup codes
            </p>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="flex items-center gap-2.5 w-full sm:w-auto">
        <button
          @click="handleRefreshDevices(false)"
          :disabled="isDevicesLoading"
          class="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-white hover:bg-slate-50 active:scale-[0.98] border border-slate-200 text-slate-700 text-sm font-semibold shadow-sm hover:shadow transition-all disabled:opacity-50 disabled:pointer-events-none"
        >
          <svg
            v-if="!isDevicesLoading"
            class="w-4 h-4 text-slate-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          <div
            v-else
            class="w-4 h-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin"
          ></div>
          <span>{{ isDevicesLoading ? "Loading..." : "Refresh" }}</span>
        </button>

        <button
          @click="isModalOpen = true"
          class="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 active:scale-[0.98] text-white text-sm font-semibold shadow-sm hover:shadow-md transition-all"
        >
          <svg
            class="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 4v16m8-8H4"
            />
          </svg>
          <span>Book Device</span>
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div
      v-if="isDevicesLoading"
      class="flex flex-col items-center justify-center py-24 bg-white rounded-3xl border border-slate-100 shadow-sm"
    >
      <div
        class="w-10 h-10 border-3 border-blue-100 border-t-blue-600 rounded-full animate-spin mb-3"
      ></div>
      <p class="text-slate-500 font-medium text-sm">Loading your devices...</p>
    </div>

    <!-- Empty State -->
    <div
      v-else-if="devices.length === 0"
      class="text-center py-20 bg-white rounded-3xl border border-slate-100 shadow-sm"
    >
      <div
        class="w-16 h-16 rounded-2xl bg-slate-50 text-slate-300 flex items-center justify-center mx-auto mb-4"
      >
        <Cpu class="w-8 h-8" />
      </div>
      <h3 class="text-base font-semibold text-slate-800 mb-1">
        No devices booked yet
      </h3>
      <p class="text-sm text-slate-400 max-w-sm mx-auto mb-5">
        Book your first smart RFID terminal to begin recording NFC attendance.
      </p>
      <button
        @click="isModalOpen = true"
        class="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium text-sm shadow-sm transition-all"
      >
        Book Device Now
      </button>
    </div>

    <!-- Device Cards Grid -->
    <div
      v-else
      class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
    >
      <div
        v-for="device in devices"
        :key="device._id"
        class="group bg-white rounded-2xl border border-slate-200/80 hover:border-slate-300 shadow-sm hover:shadow-lg transition-all duration-200 flex flex-col justify-between overflow-hidden"
      >
        <div class="p-5">
          <!-- Top Header Row -->
          <div class="flex items-start justify-between gap-3 mb-4">
            <div class="min-w-0 flex-1">
              <h3
                class="text-base font-bold text-slate-900 truncate group-hover:text-blue-600 transition-colors"
              >
                {{ device.label ? device.label : device.deviceName }}
              </h3>

              <div
                class="flex items-center gap-2 text-xs text-slate-400 mt-0.5"
              >
                <span
                  v-if="device.label"
                  class="truncate font-medium text-slate-600"
                >
                  {{ device.deviceName }}
                </span>
                <span
                  v-if="device.label"
                  class="text-slate-300"
                  >•</span
                >
                <span class="font-mono">ID: {{ device.chipId || "N/A" }}</span>
              </div>
            </div>

            <!-- Menu Dropdown -->
            <div class="relative flex-shrink-0">
              <button
                @click="toggleMenu(device._id)"
                class="p-2 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
              >
                <svg
                  class="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M12 8c1.1 0 2-1 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 1-2 2s.9 2 2 2 2-1 2-2-1-2-2-2zm0 6c-1.1 0-2 1-2 2s.9 2 2 2 2-1 2-2-1-2-2-2z"
                  />
                </svg>
              </button>

              <transition name="fade-scale">
                <div
                  v-if="openMenus[device._id]"
                  class="absolute right-0 mt-1.5 w-52 bg-white rounded-2xl shadow-xl border border-slate-100 py-1.5 z-30 ring-1 ring-slate-900/5"
                  @click.stop
                >
                  <button
                    @click="
                      handleGenerateNewCode(device);
                      openMenus[device._id] = false;
                    "
                    :disabled="generatingCodeId === device._id"
                    class="w-full text-left px-4 py-2.5 text-blue-600 hover:bg-blue-50 disabled:opacity-50 disabled:pointer-events-none transition-colors flex items-center gap-2.5 text-xs font-semibold"
                  >
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                    {{
                      generatingCodeId === device._id
                        ? "Generating..."
                        : "Generate Setup Code"
                    }}
                  </button>

                  <button
                    @click="
                      openEditModal(device);
                      openMenus[device._id] = false;
                    "
                    class="w-full text-left px-4 py-2.5 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2.5 text-xs font-semibold"
                  >
                    <svg
                      class="w-4 h-4 text-slate-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                    Edit Device
                  </button>

                  <div class="my-1 border-t border-slate-100"></div>

                  <button
                    @click="
                      openFactoryResetWarning(device);
                      openMenus[device._id] = false;
                    "
                    class="w-full text-left px-4 py-2.5 text-rose-600 hover:bg-rose-50 transition-colors flex items-center gap-2.5 text-xs font-semibold"
                  >
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 9v2m0 4v2m0 0v2m0-6v2m0-6H9m3 0h3m0 0h3M9 12H6m3 0h3m0 0h3"
                      />
                    </svg>
                    Factory Reset
                  </button>
                </div>
              </transition>
            </div>
          </div>

          <!-- Setup Code / Last Setup Section -->
          <div
            class="rounded-2xl p-4 bg-slate-50 border border-slate-200/80 mb-4"
          >
            <!-- If Code Exists -->
            <template v-if="device.code && !isCodeExpired(device)">
              <div class="flex items-center justify-between mb-2">
                <span
                  class="text-[11px] font-bold text-blue-600 uppercase tracking-wider"
                >
                  Setup Code
                </span>
                <span
                  class="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-100/80 px-2 py-0.5 rounded-full"
                >
                  <span
                    class="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"
                  ></span>
                  {{ getRemainingTime(device.setupCodeExpiresAt) }}
                </span>
              </div>

              <!-- Code Clickable Box -->
              <div
                @click="copyToClipboard(device)"
                class="group/code flex items-center justify-between bg-white rounded-xl px-4 py-2.5 border border-slate-200 hover:border-blue-500 hover:shadow-sm cursor-pointer transition-all duration-200"
              >
                <code
                  class="font-mono font-extrabold text-xl tracking-[0.25em] text-slate-900 group-hover/code:text-blue-600 transition-colors"
                >
                  {{ device.code }}
                </code>
                <div
                  class="p-1 rounded-lg text-slate-400 group-hover/code:text-blue-600"
                >
                  <svg
                    v-if="!copiedMap[device._id]"
                    class="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                  <svg
                    v-else
                    class="w-4 h-4 text-emerald-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </div>
              </div>
              <p class="text-[11px] text-slate-400 mt-2">
                Click to copy code for terminal pairing
              </p>
            </template>

            <!-- If Installed / No Active Code -->
            <template v-else>
              <div class="flex items-center justify-between mb-1.5">
                <span
                  class="text-[11px] font-bold text-slate-500 uppercase tracking-wider"
                >
                  Installation
                </span>
                <span
                  v-if="device.lastSetupAt"
                  class="text-[11px] font-semibold text-emerald-700 bg-emerald-100/80 px-2 py-0.5 rounded-full"
                >
                  Installed
                </span>
              </div>

              <p class="text-sm font-semibold text-slate-800">
                {{
                  device.lastSetupAt
                    ? new Date(device.lastSetupAt).toLocaleString(undefined, {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })
                    : "Not paired yet"
                }}
              </p>
              <p class="text-[11px] text-slate-400 mt-1">
                {{
                  device.lastSetupAt
                    ? formatRelativeTime(device.lastSetupAt)
                    : "Generate code to install hardware."
                }}
              </p>
            </template>
          </div>

          <!-- Attributes Grid -->
          <div class="grid grid-cols-2 gap-2.5">
            <div class="bg-slate-50 rounded-xl p-2.5 border border-slate-100">
              <span
                class="block text-[10px] font-bold text-slate-400 uppercase tracking-wider"
                >Type</span
              >
              <span class="text-xs font-semibold text-slate-700"
                >Smart Reader</span
              >
            </div>

            <div
              class="bg-slate-50 rounded-xl p-2.5 border border-slate-100 flex flex-col justify-center"
            >
              <span
                class="block text-[10px] font-bold text-slate-400 uppercase tracking-wider"
                >Status</span
              >
              <span
                :class="[
                  'text-xs font-bold capitalize',
                  device.status === 'active'
                    ? 'text-emerald-600'
                    : device.status === 'disabled'
                    ? 'text-rose-600'
                    : 'text-amber-600',
                ]"
              >
                {{ device.status || "Pending" }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Book Device Modal -->
    <div
      v-if="isModalOpen"
      class="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      @click.self="isModalOpen = false"
    >
      <div
        class="bg-white rounded-3xl shadow-xl max-w-md w-full p-6 border border-slate-100"
      >
        <div class="flex items-center justify-between mb-5">
          <h2 class="text-xl font-bold text-slate-900">Book New Devices</h2>
          <button
            @click="isModalOpen = false"
            class="p-1 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div
          v-if="bookingSuccess"
          class="mb-4 p-3.5 bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold rounded-xl flex items-center gap-2"
        >
          <svg
            class="w-4 h-4 text-emerald-600 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clip-rule="evenodd"
            />
          </svg>
          Devices booked successfully!
        </div>

        <div
          v-if="bookingError"
          class="mb-4 p-3.5 bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold rounded-xl flex items-center gap-2"
        >
          <svg
            class="w-4 h-4 text-rose-600 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 001.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clip-rule="evenodd"
            />
          </svg>
          {{ bookingError }}
        </div>

        <form
          @submit.prevent="handleBookDevices"
          class="space-y-4"
        >
          <div>
            <label
              class="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5"
            >
              Number of Devices
            </label>
            <input
              v-model.number="bookForm.numberOfDevices"
              type="number"
              min="1"
              placeholder="e.g., 5"
              class="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 text-sm placeholder-slate-400 focus:outline-none focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-500/10 transition"
            />
          </div>

          <button
            type="submit"
            :disabled="isBookingLoading"
            class="w-full py-2.5 px-4 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:pointer-events-none text-white font-semibold text-sm rounded-xl transition shadow-sm"
          >
            {{ isBookingLoading ? "Booking..." : "Confirm Booking" }}
          </button>
        </form>

        <button
          @click="isModalOpen = false"
          class="w-full mt-2.5 py-2.5 px-4 text-slate-600 hover:bg-slate-100 font-medium text-sm rounded-xl transition"
        >
          Cancel
        </button>
      </div>
    </div>

    <!-- Edit Device Modal -->
    <div
      v-if="isEditModalOpen"
      class="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      @click.self="isEditModalOpen = false"
    >
      <div
        class="bg-white rounded-3xl shadow-xl max-w-md w-full p-6 border border-slate-100"
      >
        <div class="flex items-center justify-between mb-5">
          <h2 class="text-xl font-bold text-slate-900">Edit Device</h2>
          <button
            @click="isEditModalOpen = false"
            class="p-1 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div
          v-if="editError"
          class="mb-4 p-3.5 bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold rounded-xl flex items-center gap-2"
        >
          <svg
            class="w-4 h-4 text-rose-600 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 001.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clip-rule="evenodd"
            />
          </svg>
          {{ editError }}
        </div>

        <div
          v-if="editSuccess"
          class="mb-4 p-3.5 bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold rounded-xl flex items-center gap-2"
        >
          <svg
            class="w-4 h-4 text-emerald-600 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clip-rule="evenodd"
            />
          </svg>
          Device updated successfully!
        </div>

        <form
          @submit.prevent="handleEditDevice"
          class="space-y-4"
        >
          <div>
            <label
              class="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5"
            >
              Device Name
            </label>
            <input
              v-model="editForm.deviceName"
              type="text"
              placeholder="e.g., Main Gate Reader"
              class="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 text-sm placeholder-slate-400 focus:outline-none focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-500/10 transition"
            />
          </div>

          <div>
            <label
              class="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5"
            >
              Label (Optional)
            </label>
            <input
              v-model="editForm.label"
              type="text"
              placeholder="e.g., Block B Entrance"
              class="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 text-sm placeholder-slate-400 focus:outline-none focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-500/10 transition"
            />
          </div>

          <button
            type="submit"
            :disabled="isEditLoading"
            class="w-full py-2.5 px-4 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:pointer-events-none text-white font-semibold text-sm rounded-xl transition shadow-sm"
          >
            {{ isEditLoading ? "Saving..." : "Save Changes" }}
          </button>
        </form>

        <button
          @click="isEditModalOpen = false"
          class="w-full mt-2.5 py-2.5 px-4 text-slate-600 hover:bg-slate-100 font-medium text-sm rounded-xl transition"
        >
          Cancel
        </button>
      </div>
    </div>

    <!-- Factory Reset Warning Modal -->
    <div
      v-if="isFactoryResetWarningOpen"
      class="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      @click.self="isFactoryResetWarningOpen = false"
    >
      <div
        class="bg-white rounded-3xl shadow-xl max-w-md w-full p-6 border border-slate-100"
      >
        <div class="flex items-center justify-between mb-5">
          <div class="flex items-center gap-3">
            <div class="p-2 bg-rose-50 text-rose-600 rounded-xl">
              <svg
                class="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 9v2m0 4v2m0 0v2m0-6v2m0-6H9m3 0h3m0 0h3M9 12H6m3 0h3m0 0h3"
                />
              </svg>
            </div>
            <h2 class="text-xl font-bold text-slate-900">Factory Reset</h2>
          </div>
          <button
            @click="isFactoryResetWarningOpen = false"
            class="p-1 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div
          class="mb-5 p-3.5 bg-rose-50 border border-rose-200/80 rounded-2xl"
        >
          <h3
            class="text-xs font-bold text-rose-900 uppercase tracking-wide mb-1"
          >
            Warning
          </h3>
          <p class="text-rose-800 text-xs leading-relaxed">
            This will erase all network settings and offline caches from the
            hardware. The device will revert to a setup state.
          </p>
        </div>

        <div
          class="mb-5 p-3.5 bg-slate-50 rounded-2xl border border-slate-200/80"
        >
          <span
            class="block text-[10px] font-bold text-slate-400 uppercase tracking-wider"
            >Target Device</span
          >
          <p class="text-sm font-bold text-slate-900">
            {{ selectedDeviceForReset?.deviceName }}
          </p>
          <p class="text-xs text-slate-500 font-mono mt-0.5">
            ID: {{ selectedDeviceForReset?.chipId || "N/A" }}
          </p>
        </div>

        <div
          v-if="factoryResetError"
          class="mb-4 p-3.5 bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold rounded-xl flex items-start gap-2"
        >
          <svg
            class="w-4 h-4 mt-0.5 flex-shrink-0 text-rose-600"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 001.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clip-rule="evenodd"
            />
          </svg>
          <span>{{ factoryResetError }}</span>
        </div>

        <div class="flex gap-2.5 flex-col sm:flex-row">
          <button
            @click="isFactoryResetWarningOpen = false"
            class="flex-1 py-2.5 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-sm rounded-xl transition"
          >
            Cancel
          </button>
          <button
            @click="handleFactoryReset"
            :disabled="
              isFactoryResetLoading ||
              selectedDeviceForReset?.status !== 'active'
            "
            class="flex-1 py-2.5 px-4 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 disabled:pointer-events-none text-white font-semibold text-sm rounded-xl transition shadow-sm"
          >
            {{
              isFactoryResetLoading
                ? "Sending Reset..."
                : selectedDeviceForReset?.status !== "active"
                ? "Only Active Devices"
                : "Confirm Reset"
            }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import {
    ref,
    computed,
    onMounted,
    onUnmounted,
    onActivated,
    onDeactivated,
  } from "vue";
  import { useStore } from "vuex";
  import { useGlobalToast } from "@/utils/useGlobalToast";
  import { Cpu } from "lucide-vue-next";

  const { showToast } = useGlobalToast();
  const store = useStore();

  const isModalOpen = ref(false);
  const openMenus = ref({});
  const now = ref(Date.now());
  let timerInterval = null;
  let autoRefreshInterval = null;
  const bookForm = ref({
    schoolName: "",
    numberOfDevices: "",
    numberOfStudents: "",
  });

  const isBookingLoading = ref(false);
  const bookingSuccess = ref(false);
  const bookingError = ref("");

  const isDevicesLoading = ref(false);
  const copiedMap = ref({});
  const generatingCodeId = ref(null);

  const isFactoryResetWarningOpen = ref(false);
  const isFactoryResetLoading = ref(false);
  const factoryResetError = ref("");
  const selectedDeviceForReset = ref(null);
  let pairingInterval = null;

  const isEditModalOpen = ref(false);
  const isEditLoading = ref(false);
  const editError = ref("");
  const editSuccess = ref(false);
  const selectedDeviceForEdit = ref(null);
  const editForm = ref({
    deviceName: "",
    label: "",
  });

  const devices = computed(() => store.state.devices || []);

  const toggleMenu = (deviceId) => {
    openMenus.value[deviceId] = !openMenus.value[deviceId];
  };

  const copyToClipboard = async (device) => {
    try {
      await navigator.clipboard.writeText(device.code);

      copiedMap.value[device._id] = true;

      setTimeout(() => {
        delete copiedMap.value[device._id];
      }, 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const openEditModal = (device) => {
    selectedDeviceForEdit.value = device;
    editForm.value = {
      deviceName: device.deviceName || "",
      label: device.label || "",
    };
    editError.value = "";
    editSuccess.value = false;
    isEditModalOpen.value = true;
  };

  const handleEditDevice = async () => {
    if (!selectedDeviceForEdit.value) return;

    editError.value = "";
    editSuccess.value = false;

    if (!editForm.value.deviceName.trim()) {
      editError.value = "Device name is required";
      return;
    }

    isEditLoading.value = true;

    try {
      await store.dispatch("updateDeviceDetails", {
        deviceId: selectedDeviceForEdit.value._id,
        deviceName: editForm.value.deviceName.trim(),
        label: editForm.value.label.trim() || undefined,
      });

      editSuccess.value = true;
      showToast("success", "Device updated successfully!");

      setTimeout(() => {
        isEditModalOpen.value = false;
        editSuccess.value = false;
      }, 1500);

      await handleRefreshDevices();
    } catch (error) {
      console.error("Failed to update device:", error);
      editError.value =
        error.response?.data?.error ||
        error.message ||
        "Failed to update device. Please try again.";
      showToast("error", editError.value);
    } finally {
      isEditLoading.value = false;
    }
  };

  const openFactoryResetWarning = (device) => {
    selectedDeviceForReset.value = device;
    factoryResetError.value = "";
    isFactoryResetWarningOpen.value = true;
  };

  const handleGenerateNewCode = async (device) => {
    if (!device || !device._id) return;

    generatingCodeId.value = device._id;

    try {
      const res = await store.dispatch("generateDeviceSetupCode", device._id);

      console.log("Generate new code success:", res);

      await handleRefreshDevices();
      showToast("success", "New setup code generated successfully!");
      startPairingWatcher(device._id);
    } catch (error) {
      console.error("Failed to generate new code:", error);

      showToast(
        "error",
        error?.response?.data?.error || "Failed to generate new code",
      );
    } finally {
      generatingCodeId.value = null;
    }
  };

  const handleFactoryReset = async () => {
    if (!selectedDeviceForReset.value) return;

    factoryResetError.value = "";
    isFactoryResetLoading.value = true;

    try {
      await store.dispatch("factoryReset", {
        deviceId: selectedDeviceForReset.value._id,
      });

      showToast(
        "success",
        "Reset command sent. Device will reset within a few seconds.",
      );

      isFactoryResetWarningOpen.value = false;

      const watchId = selectedDeviceForReset.value._id;

      const waitForDeviceReset = setInterval(async () => {
        await handleRefreshDevices(true);

        const updated = devices.value.find((d) => d._id === watchId);

        if (!updated || updated.status === "pending") {
          clearInterval(waitForDeviceReset);
          showToast("success", "Device reset completed and ready for setup.");
        }
      }, 3000);
    } catch (error) {
      console.error("❌ Failed factory reset:", error);
      factoryResetError.value =
        error.message ||
        "Failed to reset device. Please try again or contact support.";
    } finally {
      isFactoryResetLoading.value = false;
    }
  };

  const handleBookDevices = async () => {
    bookingError.value = "";
    bookingSuccess.value = false;

    if (!bookForm.value.numberOfDevices) {
      bookingError.value = "Please fill in all fields";
      return;
    }

    isBookingLoading.value = true;

    try {
      await store.dispatch("bookDevices", {
        numberOfDevices: bookForm.value.numberOfDevices,
      });

      bookingSuccess.value = true;
      bookForm.value = {
        numberOfDevices: "",
      };

      setTimeout(() => {
        bookingSuccess.value = false;
        isModalOpen.value = false;
      }, 2000);
      showToast("success", "Devices booked successfully!");

      await handleRefreshDevices();
    } catch (error) {
      bookingError.value = error.message || "Failed to book devices";
      showToast("error", bookingError.value);
    } finally {
      isBookingLoading.value = false;
    }
  };

  const handleRefreshDevices = async (silent = false) => {
    if (!silent) {
      isDevicesLoading.value = true;
    }

    try {
      const res = await store.dispatch("fetchMyDevices");

      const anyMenuOpen = Object.values(openMenus.value).some(Boolean);

      if (!anyMenuOpen) {
        const deviceWithoutCode = devices.value.find((d) => !d.code);
        if (deviceWithoutCode) {
          openMenus.value[deviceWithoutCode._id] = true;
        }
      }

      if (res?.serverTime) {
        now.value = new Date(res.serverTime).getTime();
      } else {
        now.value = Date.now();
      }
    } catch (error) {
      console.error("Failed to fetch devices:", error);
    } finally {
      if (!silent) {
        isDevicesLoading.value = false;
      }
    }
  };

  const getRemainingTime = (expiry) => {
    if (!expiry) return null;

    const diff = new Date(expiry).getTime() - now.value;
    if (diff <= 0) return "Expired";

    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);

    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;

    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const isCodeExpired = (device) => {
    if (!device?.setupCodeExpiresAt) return false;
    return new Date(device.setupCodeExpiresAt).getTime() - now.value <= 0;
  };

  const startPairingWatcher = (deviceId) => {
    if (pairingInterval) clearInterval(pairingInterval);

    pairingInterval = setInterval(async () => {
      await handleRefreshDevices(true);

      const updated = devices.value.find((d) => d._id === deviceId);
      if (!updated) {
        clearInterval(pairingInterval);
        pairingInterval = null;
        return;
      }

      if (updated.status === "active") {
        clearInterval(pairingInterval);
        pairingInterval = null;
        showToast("success", "Device successfully paired!");
        return;
      }

      if (isCodeExpired(updated)) {
        clearInterval(pairingInterval);
        pairingInterval = null;
        return;
      }
    }, 5000);
  };

  const startTimer = () => {
    stopTimer();

    timerInterval = setInterval(() => {
      now.value = Date.now();
    }, 1000);
  };

  const stopTimer = () => {
    if (timerInterval) {
      clearInterval(timerInterval);
      timerInterval = null;
    }
  };

  const startAutoRefresh = () => {
    stopAutoRefresh();

    autoRefreshInterval = setInterval(async () => {
      const hasActiveCountdown = devices.value.some((device) => {
        const notExpired =
          device.setupCodeExpiresAt &&
          new Date(device.setupCodeExpiresAt).getTime() > Date.now();

        return device.status === "pending" && notExpired;
      });

      if (!hasActiveCountdown) {
        stopAutoRefresh();
        return;
      }

      await handleRefreshDevices(true);
    }, 5000);
  };

  const stopAutoRefresh = () => {
    if (autoRefreshInterval) {
      clearInterval(autoRefreshInterval);
      autoRefreshInterval = null;
    }
  };

  const formatRelativeTime = (date) => {
    const diff = Date.now() - new Date(date).getTime();

    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
    if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
    return `${days} day${days > 1 ? "s" : ""} ago`;
  };

  onMounted(async () => {
    await handleRefreshDevices();
    startTimer();
    startAutoRefresh();
  });

  onActivated(async () => {
    startTimer();
    startAutoRefresh();
  });

  onDeactivated(() => {
    stopTimer();
    stopAutoRefresh();
  });

  onUnmounted(() => {
    stopTimer();
    stopAutoRefresh();

    if (pairingInterval) {
      clearInterval(pairingInterval);
      pairingInterval = null;
    }
  });
</script>

<style scoped>
  .fade-scale-enter-active,
  .fade-scale-leave-active {
    transition: all 0.15s ease-out;
  }

  .fade-scale-enter-from,
  .fade-scale-leave-to {
    opacity: 0;
    transform: scale(0.95) translateY(-4px);
  }
</style>
